import { useListState } from '@mantine/hooks';
import { useState, useMemo, useEffect, createContext, useContext } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Contact, ContactS } from '../ContactS';
import { EventParticipationS } from '../EventParticipantS';
import { EventParticipationDto, EventRole } from '../../Types/EventT';
import { useContact, useContacts } from '../hooks/useContact';
import { MessageDTO } from '../../Types/MessageT';
import { PostboxCountersProvider } from './PostboxCountersContext';
import { EventContext } from './EventContext';
import { EventLocationContext } from './EventLocationContextV2';
import { CustomAttributesContext } from './CustomAttributeContext';

interface Props {
  loading: boolean;
  adding: boolean;
  mainCustomerId?: number;
  customer: Contact;
  participations: EventParticipationDto[];
  contacts: Contact[];
  patchCustomer: (patch: Partial<Contact>) => void;
  saveParticipant: (contactId: number, eventRole?: EventRole) => void;
  updateParticipant: (contact: Contact) => void;
  removeParticipation: (index: number) => void;
  dispatchSentMessage: (message: MessageDTO) => void;
}

export const EventParticipationsContext = createContext({} as Props);

export const EventParticipationsProvider = () => {
  const { id: eventId } = useParams();
  const [operationStatus, setStatus] = useState({
    loading: false,
    adding: false,
  });

  const [participants, handlers] = useListState<EventParticipationDto>([]);
  const contactIds = useMemo(() => participants.map((p) => p.contactId), [participants]);
  const { contacts, update: updateContact } = useContacts(contactIds);

  const mainCustomer = useMemo(() => participants.find((p) => p.eventRole === 'MAIN_CUSTOMER'), [participants]);
  const { loading, adding } = operationStatus;
  const { setState, append, remove } = handlers;

  useEffect(() => {
    fetchParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchParticipants = () => {
    if (eventId) {
      setStatus((prev) => ({ ...prev, loading: true }));
      EventParticipationS.getByEventId(eventId)
        .then(setState)
        .finally(() => setStatus((prev) => ({ ...prev, loading: false })));
    }
  };

  const saveParticipant = async (contactId: number, eventRole?: EventRole) => {
    // Adjust call methods
    const resJSON = await EventParticipationS.save({ contactId, eventRole, eventId: Number(eventId) });
    if (resJSON) {
      fetchParticipants();
    }
  };

  const updateParticipant = (contact: Contact) => updateContact(contact);

  const patchCustomer = (patch: Partial<Contact>) => {
    if (mainCustomer) {
      ContactS.edit(mainCustomer.contactId, patch).then((resJSON) => {
        if (resJSON) {
          updateContact(resJSON);
        }
      });
    }
  };

  const removeParticipation = (index: number) => remove(index);

  const dispatchSentMessage = (message: MessageDTO) => {
    (async () => {
      const recipients = [...message.to, ...message.cc, ...message.bcc];
      const directContactIds = recipients
        .map((recipient) => recipient?.contactId)
        .filter((contactId): contactId is number => Number.isFinite(contactId) && contactId > 0);

      const emailsNeedingLookup = recipients
        .filter((recipient) => !recipient?.contactId && !!recipient?.email)
        .map((recipient) => recipient.email.trim().toLowerCase())
        .filter((email, index, arr) => email !== '' && arr.indexOf(email) === index);

      const resolvedContactIds = emailsNeedingLookup.length > 0 ? await ContactS.findByEmails(emailsNeedingLookup) : [];

      const allMessageContactIds = [...directContactIds, ...resolvedContactIds];
      const uniqueMessageContactIds = allMessageContactIds.filter(
        (contactId, index, arr) => Number.isFinite(contactId) && contactId > 0 && arr.indexOf(contactId) === index,
      );

      const existingParticipantIds = new Set(participants.map((participant) => participant.contactId));
      const missingParticipantIds = uniqueMessageContactIds.filter(
        (contactId) => !existingParticipantIds.has(contactId),
      );

      if (!eventId || missingParticipantIds.length === 0) {
        return;
      }

      const responses = await Promise.all(
        missingParticipantIds.map((contactId) => EventParticipationS.save({ contactId, eventId: Number(eventId) })),
      );

      if (responses.some(Boolean)) {
        fetchParticipants();
      }
    })();
  };

  const value = useMemo(
    () => ({
      loading,
      adding,
      participations: participants,
      mainCustomerId: mainCustomer?.contactId,
      customer: contacts.find((c) => c.id === mainCustomer?.contactId) ?? ({} as Contact),
      contacts,
      patchCustomer,
      updateParticipant,
      saveParticipant,
      removeParticipation,
      dispatchSentMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, adding, participants, contacts],
  );

  return (
    <EventParticipationsContext.Provider value={value}>
      <PostboxCountersProvider contactsIds={contactIds.length === 0 ? null : contactIds}>
        <Outlet />
      </PostboxCountersProvider>
    </EventParticipationsContext.Provider>
  );
};

export const useEventCustomer = (id?: number) => {
  const ctx = useContext(EventParticipationsContext);
  const { contact, editContact } = useContact(window.location.pathname.includes('events') ? null : id);
  if (ctx && window.location.pathname.includes('events')) {
    const { customer, patchCustomer } = ctx;
    return { customer, patchCustomer };
  }
  return { customer: contact, patchCustomer: editContact };
};

export const useEventData = () => {
  const eventCtx = useContext<any>(EventContext);
  const { event } = eventCtx ?? {};
  const { customer } = useContext(EventParticipationsContext);
  const { location, distance } = useContext(EventLocationContext);
  const customAttributeValuesCtx = useContext<any>(CustomAttributesContext);
  const { customAttributeValues } = customAttributeValuesCtx ?? {};

  return {
    event,
    customer,
    location,
    distance,
    customAttributeValues,
  };
};
